import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import { useEvents } from '../../contexts/EventContext';
import { isEventStillVisible } from '../../utils/helpers';
import {
  DEFAULT_FILTERS,
  EventFilters,
  filterEvents,
  groupEventsByBucket,
  getDistinctLocations,
  isAnyFilterActive,
} from '../../utils/eventFiltering';
import EventFilterBar from '../../components/events/EventFilterBar';
import EventSection from '../../components/events/EventSection';

const EventsPage: React.FC = () => {
  const navigate = useNavigate();
  const { events } = useEvents();
  const [filters, setFilters] = useState<EventFilters>(DEFAULT_FILTERS);

  const visibleEvents = useMemo(
    () => events.filter((e) => e.isActive && isEventStillVisible(e.date, e.endTime)),
    [events]
  );

  // Location options come from the SAME visible-event set the list is built
  // from, so locations whose only events have ended never appear as options.
  const dens = useMemo(() => getDistinctLocations(visibleEvents), [visibleEvents]);

  const filtered = filterEvents(visibleEvents, filters);
  const grouped = groupEventsByBucket(filtered, new Date());
  const totalCount = filtered.length;

  const handleViewEvent = (eventId: string) => {
    navigate(`/events/${eventId}`);
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={600} sx={{ mb: 1 }}>
        Events
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Browse and register for upcoming Team Jak events
      </Typography>

      <EventFilterBar filters={filters} dens={dens} onChange={setFilters} />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
        Showing {totalCount} event{totalCount !== 1 ? 's' : ''}
      </Typography>

      {totalCount === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            backgroundColor: '#f5f5f5',
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No events found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search or filter criteria
          </Typography>
          {isAnyFilterActive(filters) && (
            <Button sx={{ mt: 2 }} onClick={() => setFilters(DEFAULT_FILTERS)}>
              Clear filters
            </Button>
          )}
        </Box>
      ) : (
        <>
          <EventSection title="Next 7 days" events={grouped.next7} onView={handleViewEvent} />
          <EventSection title="Next 30 days" events={grouped.next30} onView={handleViewEvent} />
          <EventSection title="Later" events={grouped.later} onView={handleViewEvent} />
        </>
      )}
    </Box>
  );
};

export default EventsPage;
